import axios from 'axios';
import { mockDocumentNumberRules } from '../data/mockData.ts';

const BASE_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';
const DOCUMENT_API_URL = `${BASE_API_URL}/QmsDocument`;

export interface DocumentCreateDto {
  title: string;
  description?: string;
  category: string;
  tags?: string[];
  file?: File;
  draftFileUrl?: string;
  status: string;
  approvalSteps?: any[];
  documentNumber?: string;
  currentVersion?: string;
  createdBy?: number;
  templateId?: number | string;
}

export interface DocumentResponse {
  id: string;
  documentNumber: string;
  title: string;
  description: string;
  category: string;
  status: string;
  currentVersion: string;
  tags: string[];
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  draftFileUrl?: string;
  templateId?: string;
}

/**
 * Generate document number in format: QMS-{TypeAbbr}-{Year}-{Version}
 * Example: QMS-SOP-2026-001
 * @param category - Document category (e.g., 'Standard Operating Procedure', 'Work Instruction', 'SOP')
 * @param version - Document version (e.g., '1.0', '2.5'), will be converted to 3-digit format (001, 002, etc.)
 * @returns Generated document number
 */
export const generateDocumentNumber = (category: string, version?: string): string => {
  const currentYear = new Date().getFullYear();
  
  // Find the corresponding rule in mockDocumentNumberRules
  const rule = mockDocumentNumberRules.find(
    r => r.category === category || r.category.toLowerCase() === category.toLowerCase()
  );
  
  // Extract type abbreviation from prefix (e.g., 'QMS-SOP' -> 'SOP')
  let typeAbbr = 'DOC';
  if (rule && rule.prefix) {
    const parts = rule.prefix.split('-');
    if (parts.length > 1) {
      typeAbbr = parts[parts.length - 1]; // Get the last part after QMS-
    }
  } else {
    // If no rule found, generate abbreviation from category
    // Take first letter of each word (e.g., 'Standard Operating Procedure' -> 'SOP')
    const words = category.split(' ');
    typeAbbr = words.map(word => word.charAt(0).toUpperCase()).join('');
  }
  
  // Convert version to 3-digit sequence number
  let sequenceNum = '001';
  if (version) {
    // If version is like '1.0', extract the major version
    const majorVersion = parseInt(version.split('.')[0]);
    if (!isNaN(majorVersion) && majorVersion > 0) {
      sequenceNum = majorVersion.toString().padStart(3, '0');
    }
  }
  
  return `QMS-${typeAbbr}-${currentYear}-${sequenceNum}`;
};

/**
 * Create a new document
 * Calls the POST /api/QmsDocument endpoint with FormData
 */
export const createDocument = async (data: DocumentCreateDto): Promise<DocumentResponse> => {
  try {
    const token = localStorage.getItem('token');
    
    // Create FormData to send file and other data
    const formData = new FormData();
    formData.append('title', data.title);
    formData.append('category', data.category);
    formData.append('status', data.status);
    
    // Required fields
    const docNumber = data.documentNumber || generateDocumentNumber(data.category, data.currentVersion || '1.0');
    formData.append('documentNumber', docNumber);
    formData.append('currentVersion', data.currentVersion || '1.0');
    
    // Get current user ID from localStorage or use default
    const currentUser = localStorage.getItem('userId');
    formData.append('createdBy', data.createdBy?.toString() || currentUser || '1000');
    
    // Add templateId if provided
    if (data.templateId) {
      formData.append('templateId', data.templateId.toString());
    }
    
    if (data.description) {
      formData.append('description', data.description);
    }
    
    if (data.tags && data.tags.length > 0) {
      data.tags.forEach((tag, index) => {
        formData.append(`tags[${index}]`, tag);
      });
    }
    
    if (data.file) {
      formData.append('file', data.file);
    } else if (data.draftFileUrl) {
      // If no new file is uploaded but draftFileUrl is provided, use it
      formData.append('draftFileUrl', data.draftFileUrl);
    }
    
    if (data.approvalSteps && data.approvalSteps.length > 0) {
      formData.append('approvalSteps', JSON.stringify(data.approvalSteps));
    }

    const response = await axios.post<DocumentResponse>(DOCUMENT_API_URL, formData, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error: any) {
    console.error('Failed to create document:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to create document'
    );
  }
};

/**
 * Update an existing document
 * Calls the PUT /api/QmsDocument/{id} endpoint with FormData
 */
export const updateDocument = async (id: string, data: DocumentCreateDto): Promise<DocumentResponse> => {
  try {
    const token = localStorage.getItem('token');
    
    const formData = new FormData();
    formData.append('title', data.title);
    formData.append('category', data.category);
    formData.append('status', data.status);
    
    // Required fields
    if (data.documentNumber) {
      formData.append('documentNumber', data.documentNumber);
    }
    if (data.currentVersion) {
      formData.append('currentVersion', data.currentVersion);
    }
    
    // Get current user ID from localStorage or use default
    const currentUser = localStorage.getItem('userId');
    if (data.createdBy) {
      formData.append('createdBy', data.createdBy.toString());
    } else if (currentUser) {
      formData.append('createdBy', currentUser);
    }
    
    // Add templateId if provided
    if (data.templateId) {
      formData.append('templateId', data.templateId.toString());
    }
    
    if (data.description) {
      formData.append('description', data.description);
    }
    
    if (data.tags && data.tags.length > 0) {
      data.tags.forEach((tag, index) => {
        formData.append(`tags[${index}]`, tag);
      });
    }
    
    if (data.file) {
      formData.append('file', data.file);
    } else if (data.draftFileUrl) {
      // If no new file is uploaded, preserve existing draftFileUrl
      formData.append('draftFileUrl', data.draftFileUrl);
    }
    
    if (data.approvalSteps && data.approvalSteps.length > 0) {
      formData.append('approvalSteps', JSON.stringify(data.approvalSteps));
    }

    const response = await axios.put<DocumentResponse>(`${DOCUMENT_API_URL}/${id}`, formData, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error: any) {
    console.error('Failed to update document:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to update document'
    );
  }
};

/**
 * Get document by ID
 */
export const getDocumentById = async (id: string): Promise<DocumentResponse> => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get<DocumentResponse>(`${DOCUMENT_API_URL}/${id}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    });
    return response.data;
  } catch (error: any) {
    console.error(`Failed to get document ${id}:`, error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to get document'
    );
  }
};

/**
 * Delete document by ID
 */
export const deleteDocument = async (id: string): Promise<void> => {
  try {
    const token = localStorage.getItem('token');
    await axios.delete(`${DOCUMENT_API_URL}/${id}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    });
  } catch (error: any) {
    console.error(`Failed to delete document ${id}:`, error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to delete document'
    );
  }
};

/**
 * Submit document for approval
 * Calls the POST /api/QmsDocument/documents/{docId}/submit endpoint
 */
export const submitDocumentForApproval = async (
  docId: string | number,
  templateId?: number,
  approvalSteps?: any[]
): Promise<{ docId: number; id: number; currentStepNo: number }> => {
  try {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    
    // Add approvalStepsJson if provided
    if (approvalSteps && approvalSteps.length > 0) {
      formData.append('approvalStepsJson', JSON.stringify(approvalSteps));
    }

    const params: any = {};
    if (templateId) {
      params.templateId = templateId;
    }

    const response = await axios.post(
      `${DOCUMENT_API_URL}/documents/${docId}/submit`,
      formData,
      {
        params,
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    
    return response.data;
  } catch (error: any) {
    console.error(`Failed to submit document ${docId}:`, error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to submit document for approval'
    );
  }
};

/**
 * Commit/approve a document
 * Calls the POST /api/QmsDocument/documents/{docId}/commit endpoint
 */
export const commitDocument = async (
  docId: string | number,
  action: 'Approve' | 'Reject' = 'Approve',
  comments?: string
): Promise<{ id: number; status: string; currentStepNo: number }> => {
  try {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    
    formData.append('action', action);
    if (comments) {
      formData.append('comments', comments);
    }

    const response = await axios.post(
      `${DOCUMENT_API_URL}/documents/${docId}/commit`,
      formData,
      {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    
    return response.data;
  } catch (error: any) {
    console.error(`Failed to commit document ${docId}:`, error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to commit document'
    );
  }
};
